import { useNavigate } from '@tanstack/react-router'
import { useEffect, useRef } from 'react'
import { Alert, Button } from '../components/ui'
import { ApiError, errorMessage } from '../lib/api'
import { useExchangeInvitation } from '../lib/participants'
import { useMe } from '../lib/session'
import { SignInForm } from './sign-in'

/**
 * Borrower share link. The token is only ever posted to the API; it is never stored client-side
 * beyond this page. Signed-out visitors sign in first (with the address the link was sent to) and
 * land back here, where the exchange happens automatically.
 */
export function InvitePage({ token }: { token: string }) {
  const me = useMe()
  const navigate = useNavigate()
  const exchange = useExchangeInvitation()
  const attempted = useRef(false)
  const signedIn = me.data !== null && me.data !== undefined

  useEffect(() => {
    if (!signedIn || attempted.current) return
    attempted.current = true
    exchange.mutate(token, {
      onSuccess: ({ case_id }) => void navigate({ to: '/cases/$caseId', params: { caseId: case_id }, replace: true }),
    })
  }, [signedIn, token, exchange, navigate])

  return (
    <section className="mx-auto max-w-sm space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-neutral-900">Your financing application</h1>
        <p className="mt-1 text-sm text-neutral-500">
          A dealer has invited you to complete a financing application with Tex. Sign in with the email address the
          invitation was sent to.
        </p>
      </div>

      {me.data === null && <SignInForm redirectTo={`/invite/${token}`} />}

      {signedIn && (exchange.isPending || exchange.isIdle) && <p className="text-sm text-neutral-500">Opening your request…</p>}

      {signedIn && exchange.isError && (
        <div className="space-y-3">
          <Alert tone="error">
            {exchange.error instanceof ApiError && exchange.error.code === 'invitation_invalid' ? (
              <>
                This link is not valid for <strong>{me.data?.user.email}</strong>. It may have expired, been replaced by a newer
                one, been revoked, or been sent to a different address. Ask your dealer to send a fresh link to the address you
                signed in with.
              </>
            ) : (
              errorMessage(exchange.error)
            )}
          </Alert>
          <Button type="button" variant="secondary" onClick={() => void navigate({ to: '/' })}>
            Go to the home page
          </Button>
        </div>
      )}
    </section>
  )
}
